import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3154';

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

/** Codes système protégés (pas de delete). Libellés et actif restent éditables. */
export const U3154_SYSTEM_PAYMENT_TYPE_CODES = ['EF', 'EP', 'AP', 'ES'] as const;

export type U3154SystemPaymentTypeCode = (typeof U3154_SYSTEM_PAYMENT_TYPE_CODES)[number];

/** Réponse GET /U3154/initLU + logicalUnitCode fusionné côté shell. */
export interface U3154InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
}

/** Ligne POST /U3154/search. */
export interface U3154PaymentTypeSearchRow {
  id: string;
  code?: string;
  active?: boolean;
  paymentTypeLabelFr?: string;
  paymentTypeLabelEn?: string;
  version: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
}

export interface U3154PaymentTypeTranslation {
  id?: string;
  languageCode: string;
  paymentTypeLabel: string;
  version?: number;
}

/** Détail GET /U3154/:id (result). */
export interface U3154PaymentType {
  id: string;
  code: string;
  active: boolean;
  translations: U3154PaymentTypeTranslation[];
  version: number;
}

export type U3154PaymentTypeTranslationFormRow = U3154PaymentTypeTranslation;

export type U3154PaymentTypeFormRow = U3154PaymentType;

export interface U3154PaymentTypeCreateBody {
  code: string;
  active: boolean;
  translations: Array<{
    languageCode: string;
    paymentTypeLabel: string;
  }>;
}

export interface U3154PaymentTypeUpdateBody {
  id: string;
  active: boolean;
  version: number;
  translations: Array<
    | {
        languageCode: string;
        paymentTypeLabel: string;
      }
    | {
        id: string;
        languageCode: string;
        paymentTypeLabel: string;
        version: number;
      }
  >;
}

export interface U3154PaymentTypeDeleteBody {
  id: string;
  version: number;
}

export const U3154_ERROR_CODE_DUPLICATE = 'BU315401';
export const U3154_ERROR_CODE_IN_USE = 'BU315402';
export const U3154_ERROR_CODE_SYSTEM = 'BU315403';

export function u3154IsSystemPaymentTypeCode(code: string | null | undefined): boolean {
  if (!code) return false;
  const normalized = code.trim().toUpperCase();
  return (U3154_SYSTEM_PAYMENT_TYPE_CODES as readonly string[]).includes(normalized);
}

export function createEmptyU3154FormRow(): U3154PaymentTypeFormRow {
  return {
    id: '',
    code: '',
    active: true,
    version: 0,
    translations: [
      { languageCode: 'fr', paymentTypeLabel: '' },
      { languageCode: 'en', paymentTypeLabel: '' },
    ],
  };
}

export function normalizeU3154FromApi(row: U3154PaymentType): U3154PaymentType {
  return {
    ...row,
    active: row.active !== false,
    translations: Array.isArray(row.translations)
      ? row.translations.map((tr) => ({
          ...tr,
          languageCode: typeof tr.languageCode === 'string' ? tr.languageCode.trim().toLowerCase() : tr.languageCode,
        }))
      : [],
  };
}

function u3154TranslationLabel(
  translations: U3154PaymentTypeTranslation[] | undefined,
  lang: 'fr' | 'en',
): string | undefined {
  if (!Array.isArray(translations)) return undefined;
  const tr = translations.find((t) => t.languageCode?.trim().toLowerCase() === lang);
  const desc = tr?.paymentTypeLabel?.trim();
  return desc || undefined;
}

/** Reconstruit une ligne search (libellés FR/EN) à partir du détail fiche. */
export function u3154FormRowToSearchRow(
  row: U3154PaymentType | U3154PaymentTypeFormRow,
  existing?: U3154PaymentTypeSearchRow | null,
): U3154PaymentTypeSearchRow {
  const searchRow: U3154PaymentTypeSearchRow = {
    id: row.id,
    code: row.code,
    active: row.active !== false,
    paymentTypeLabelFr: u3154TranslationLabel(row.translations, 'fr'),
    paymentTypeLabelEn: u3154TranslationLabel(row.translations, 'en'),
    version: row.version,
  };
  if (existing) {
    return { ...existing, ...searchRow };
  }
  return searchRow;
}

export function u3154FormatPaymentTypeListLabel(row: U3154PaymentTypeSearchRow, language: string): string {
  const code = row.code?.trim() ?? '';
  const isFr = language.startsWith('fr');
  const linguistic =
    (isFr ? row.paymentTypeLabelFr : row.paymentTypeLabelEn)?.trim() ||
    row.paymentTypeLabelFr?.trim() ||
    row.paymentTypeLabelEn?.trim() ||
    '';
  if (code && linguistic) return `${code} — ${linguistic}`;
  return code || linguistic;
}

export function buildCreateBody(data: U3154PaymentTypeFormRow): U3154PaymentTypeCreateBody {
  return {
    code: data.code.trim(),
    active: data.active !== false,
    translations: data.translations.map((tr) => ({
      languageCode: tr.languageCode.trim().toLowerCase(),
      paymentTypeLabel: tr.paymentTypeLabel.trim(),
    })),
  };
}

export function buildUpdateBody(data: U3154PaymentTypeFormRow): U3154PaymentTypeUpdateBody {
  return {
    id: data.id,
    active: data.active !== false,
    version: data.version,
    translations: data.translations.map((tr) => {
      const languageCode = tr.languageCode.trim().toLowerCase();
      const paymentTypeLabel = tr.paymentTypeLabel.trim();
      const hasId = Boolean(tr.id && String(tr.id).trim() !== '');
      if (hasId && tr.id) {
        return {
          id: tr.id,
          languageCode,
          paymentTypeLabel,
          version: typeof tr.version === 'number' ? tr.version : 0,
        };
      }
      return { languageCode, paymentTypeLabel };
    }),
  };
}

export function buildDeleteBody(data: U3154PaymentTypeFormRow): U3154PaymentTypeDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3154PaymentTypeCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3154PaymentTypeUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3154PaymentTypeDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3154_ERROR_CODE_DUPLICATE:
      errorResult.fieldErrors['code'] = t('errPaymentTypeCodeDuplicate', { ns: nameSpace });
      break;
    case U3154_ERROR_CODE_IN_USE:
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errDeleteInUse', { ns: nameSpace }),
      });
      break;
    case U3154_ERROR_CODE_SYSTEM:
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errSystemPaymentType', { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
